import {cloneDeep} from 'lodash'
import {ModuleFc} from '@/project'
import {removeItem, updateAll, updateObject, findAndUpdate} from '@/store/utils'
import {Invoice, Invoices, InvoicesState, InvoicesPayload} from './types'
import issued from './modules/issued'
import external from './modules/external'
export {InvoicesState}

const initState: InvoicesState = {
  invoices: [],
  count: 0,
  amountTotal: 0,
  nextOffset: 0,
  transferredInvoices: [],
  transferredCount: 0,
  transferredNextOffset: 0,
  invoice: {
    id: null,
    status: null,
    amount: null,
    tradedAt: null,
    clientName: null,
    clientRepresentativeName: null,
    clientBizRegistrationNumber: null,
    supRepNm: null,
    clientId: '',
    isForcedPayment: false,
    paidAt: null,
    hasClientBankAccount: false,
    clientBankAccount: {
      id: null,
      bank: null,
      number: null,
      owner: null,
    },
    transferFee: null,
    bizBankAccount: {
      bankCode: null,
      number: null,
      owner: null,
    },
    clientSummary: null,
    bizSummary: null,
    supAddress: null,
    supBizNo: null,
    supEmail: null,
    itemNm: null,
    issueNo: null,
    makeDt: null,
    issueDt: null,
    sendDt: null,
    taxClsf: null,
    supAmt: null,
    totAmt: null,
    taxAmt: null,
    isTaxFree: false,
  },
}

const module: ModuleFc<InvoicesState> = (ctx) => ({
  namespaced: true,
  modules: {
    issued: issued(ctx),
    external: external(ctx),
  },
  state: cloneDeep(initState),
  actions: {
    async getInvoices({commit, rootState, state}, payload: InvoicesPayload = {}) {
      const {
        kind = 'invoice_transfer',
        bizId = rootState.biz.id,
        order,
        status = ['pending', 'failed', 'invalid', 'processing'],
        limit = 10,
        offset = Number(state.nextOffset),
        client,
      } = payload
      const result = await ctx.$axios({
        url: '/invoices',
        data: {
          bizId,
        },
        params: {
          kind,
          status,
          order,
          limit,
          offset,
          client,
        },
      })
      commit('updateInvoices', {
        list: result.data.results, offset: result.data.nextOffset, count: result.data.count
      })

    },
    async getTransferredInvoices({commit, rootState, state}, payload: InvoicesPayload = {}) {
      const {
        kind = 'invoice_transfer',
        bizId = rootState.biz.id,
        order,
        status = ['completed'],
        limit = 10,
        offset = Number(state.transferredNextOffset),
        client,
      } = payload
      const result = await ctx.$axios({
        url: '/invoices',
        data: {
          bizId,
        },
        params: {
          kind,
          status,
          order,
          limit,
          offset,
          client,
        },
      })

      commit('updateTransferredInvoices', {
        list: result.data.results, offset: result.data.nextOffset, count: result.data.count
      })
    },
    async getInvoice({commit, rootState}, payload: {bizId, id}) {
      const result = await ctx.$axios({
        url: '/invoices/$id',
        method: 'get',
        data: {
          get bizId() {
            return payload.bizId || rootState.biz.id
          },
          id: payload.id,
        },
      })
      commit('updateInvoice', result.data)
      return result.data
    },
    async updateInvoice({commit, rootState}, payload: {
      bizId, id, bizSummary, clientSummary, clientBankAccount
    }) {
      const {id, bizSummary, clientSummary, clientBankAccount} = payload
      const result = await ctx.$axios({
        url: '/invoices/$id',
        method: 'patch',
        data: {
          get bizId() {
            return payload.bizId || rootState.biz.id
          },
          id,
          bizSummary,
          clientSummary,
          clientBankAccount: clientBankAccount ? clientBankAccount : null,
        },
      })
      commit('updateInvoice', result.data)
      return result.data
    },
    async updateInvoiceStatus({commit, rootState}, payload: {
      bizId?: string, id: string, status: string
    }) {
      const {bizId = rootState.biz.id, id, status} = payload
      const result = await ctx.$axios({
        url: '/invoices/$id/status',
        method: 'post',
        data: {
          bizId,
          id,
          status,
        },
      })
      commit('updateInvoice', result.data)
    },
    async getInvoiceSummary({commit, rootState}, payload: {bizId?: string, kind?: string} = {}) {
      const {bizId = rootState.biz.id, kind = 'invoice_transfer'} = payload
      const result = await ctx.$axios({
        url: '/invoices/summary',
        method: 'get',
        params: {bizId, kind},
      })
      commit('update', result.data)
    },
  },
  mutations: {
    update(state, payload: InvoicesState) {
      updateObject(state, payload)
    },
    updateInvoices(state, payload: {list: Invoices[], offset, count}) {
      updateAll(state.invoices, payload.list)
      state.nextOffset = payload.offset
      state.count = payload.count
    },
    updateTransferredInvoices(state, payload: {list: Invoices[], offset, count}) {
      updateAll(state.transferredInvoices, payload.list)
      state.transferredNextOffset = payload.offset
      state.transferredCount = payload.count
    },
    updateInvoice(state, payload: Invoice) {
      updateObject(state.invoice, payload)
    },
    updateInvoicesItem(state, payload) {
      findAndUpdate(state.invoices, {id: payload.id}, payload)
    },
    remove(state, id: string) {
      removeItem(state.invoices, {id})
    },
    clear(state) {
      updateObject(state, initState)
    },
    clearInvoice(state) {
      state.invoice = initState.invoice
    },
    clearInvoices(state) {
      state.invoices = []
      state.nextOffset = 0
      state.count = 0
    },
    clearTransferredInvoices(state) {
      state.transferredInvoices = []
      state.transferredNextOffset = 0
      state.transferredCount = 0
    },
  },
})

export default module
